import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../auth/auth-context";

/**
 * The gate every authenticated route sits behind.
 *
 * This is convenience, not security — the server refuses an unauthenticated
 * request regardless, and that refusal is the actual control. What this buys is
 * that someone who is signed out sees the login screen instead of a screen full
 * of failed requests.
 *
 * Where they were headed is remembered, so signing in lands them there rather
 * than dumping them on the home page.
 */
export function RequireAuth(): React.JSX.Element {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "restoring") {
    return <p role="status">Signing you in…</p>;
  }

  /**
   * **To the chooser, not to the sign-in screen** (2026-09-06).
   *
   * DentaCore is one of four products now, and which one you are here for is a
   * question asked before who you are. `/products` hands the remembered
   * destination on to `/login`, so a bookmarked patient file still lands where
   * it was going. `/login` stays directly reachable for anyone who knows it.
   */
  if (status === "signed-out") {
    return <Navigate to="/products" replace state={{ from: location }} />;
  }

  /**
   * **A one-time password reaches exactly one screen** (decision SA1).
   *
   * The API refuses everything else regardless — that is the control. This is
   * what stops the reader meeting a wall of refused requests instead of the
   * page that tells them what to do.
   */
  if (user?.mustChangePassword === true && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}
